// import node module libraries
import React, { Fragment, useState, useEffect} from 'react';
import { Link } from 'react-router-dom';
import { Button, Col, Row, Tab, Card, Nav} from 'react-bootstrap';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

// import sub custom components
import GuideSettingsTable from './GuideSettingsTable';
import OffcanvasCreateGuide from './OffcanvasCreateGuide';
import OffcanvasCreateGuideCategory from './OffcanvasCreateGuideCategory';
import OffcanvasUpdateGuideCategory from './OffcanvasUpdateGuideCategory';

const AllGuideSettings = ({ activeKey }) => { // activeKey als Prop erhalten
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });

	// Guide data
	const [page, setPage] = useState(1);
	const [GuideData, setGuideData] = useState([]);
	const [guideDataLoaded, setGuideDataLoaded] = useState(false); // State to track if data is loaded
	const [isLoadingTable, setIsLoadingTable] = useState(true); // State for loading table

	// Guide Cateogry data
	const [GuideCategoryData, setGuideCategoryData] = useState([]);
	const [isLoadingGuideCategoryData, setIsLoadingGuideCategoryData] = useState(true); // State for loading spinner

	// Create Guide
	const [isLoadingButtonCreateGuide, setIsLoadingButtonCreateGuide] = useState(null); // State for loading spinner
	const [showCreateGuideOffcanvas, setShowCreateGuideOffcanvas] = useState(false);
	const handleShowCreateGuide = () => setShowCreateGuideOffcanvas(true);
	const handleCloseCreateGuide = () => setShowCreateGuideOffcanvas(false);
	const [createdNewGuide, setCreatedNewGuide] = useState(false); // State to trigger re-fetch

	// Create Guide Category
	const [isLoadingButtonCreateGuideCategory, setIsLoadingButtonCreateGuideCategory] = useState(null); // State for loading spinner
	const [showCreateGuideCategoryOffcanvas, setShowCreateGuideCategoryOffcanvas] = useState(false);
	const handleShowCreateGuideCategory = () => setShowCreateGuideCategoryOffcanvas(true);
	const handleCloseCreateGuideCategory = () => setShowCreateGuideCategoryOffcanvas(false);
	const [createdNewGuideCategory, setCreatedNewGuideCategory] = useState(false); // State to trigger re-fetch

	// fetch data from api
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	// Get data from API

	const getGuideData = async () => {
		setIsLoadingTable(true);

		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.get(`${baseURL}/public/guides?response=backend`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setGuideData({ data: responseData });
				setGuideDataLoaded(true); // Set state to true when data is loaded
			})
			.catch((error) => {
				const errorMessage = error.response?.data?.message
					? error.message + '. ' + error.response.data.message
					: error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
				setIsLoadingTable(false);
			});
	}

	const getGuideCategoryData = async () => {
		setIsLoadingGuideCategoryData(true);
		setIsLoadingTable(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.get(`${baseURL}/public/guides/categories`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setGuideCategoryData(responseData);
				GuideData.data.forEach(guide => {
					const matchingCategory = GuideCategoryData?.find(category => category.id === guide.guide_category_id);
					if (matchingCategory) {
						setGuideData(prevState => ({
							...prevState,
							data: prevState.data.map(item =>
								item.id === guide.id ? { ...item, guide_category_title: matchingCategory.guide_category_title } : item
							)
						}));
					}
				});
				setIsLoadingGuideCategoryData(false);
				setIsLoadingTable(false);
			})
			.catch((error) => {
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
				setIsLoadingGuideCategoryData(false);
				setIsLoadingTable(false);
			});
	};

	useEffect(() => {
		if (activeKey === 'guides-and-ressources') {
			getGuideData();
			setGuideDataLoaded(false); // Reset the state
		}
	}, [activeKey, createdNewGuide, createdNewGuideCategory]);

	useEffect(() => {
		if (activeKey === 'guides-and-ressources') {
			getGuideCategoryData();
		}
	}, [guideDataLoaded]);

	// Create Guide
	const handleCreateGuide = async (e) => {
		e.preventDefault();
		setIsLoadingButtonCreateGuide(true);

		const data = {
			guide_title: e.target.title.value,
			guide_slug: e.target.title_slug.value,
			guide_content: e.target.content.value,
			guide_category_id: e.target.category.value
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.post(`${baseURL}/public/guides`, data, options)
			.then(response => {
				// Trigger a re-fetch of the data
				setCreatedNewGuide(true);
				// Close the offcanvas
				setShowCreateGuideOffcanvas(false);
				setIsLoadingButtonCreateGuide(false);
				// Reset the form fields
				e.target.reset();
				// Show success notification
				handleShowNotification(setNotification, 'success', 'Erfolgreich', 'Deine Daten wurden erfolgreich gespeichert');
			})
			.catch((error) => {
				setShowCreateGuideOffcanvas(true);
				setIsLoadingButtonCreateGuide(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};

	// Create Guide Category
	const [selectedGuideCategory, setSelectedGuideCateory] = useState({}); // State to store selected row data

	const handleCreateGuideCategory = async (e) => {
		e.preventDefault();
		setIsLoadingButtonCreateGuideCategory(true);

		const data = {
			guide_category_title: e.target.title_category.value,
			guide_category_slug: e.target.title_slug_category.value,
			guide_category_description: e.target.description_category.value
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.post(`${baseURL}/public/guides/categories`, data, options)
			.then(response => {
				// Trigger a re-fetch of the data
				setCreatedNewGuideCategory(true);
				// Close the offcanvas
				setShowCreateGuideCategoryOffcanvas(false);
				setIsLoadingButtonCreateGuideCategory(false);
				// Reset the form fields
				e.target.reset();
				// Show success notification
				handleShowNotification(setNotification, 'success', 'Erfolgreich', 'Deine Daten wurden erfolgreich gespeichert');
			})
			.catch((error) => {
				setShowCreateGuideCategoryOffcanvas(true);
				setIsLoadingButtonCreateGuideCategory(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};

	// Update Guide Category
	const [isLoadingButtonUpdateGuideCategory, setIsLoadingButtonUpdateGuideCategory] = useState(null); // State for loading spinner
	const [showUpdateGuideCategoryOffcanvas, setShowUpdateGuideCategoryOffcanvas] = useState(false);
	const handleShowUpdateGuideCategory = (category) => {
		setSelectedGuideCateory(category);
		setShowUpdateGuideCategoryOffcanvas(true);
	}
		
	const handleCloseUpdateGuideCategory = () => {
		setShowUpdateGuideCategoryOffcanvas(false);
		setSelectedGuideCateory({});
	};

	const handleUpdateGuideCategory = async (e) => {
		e.preventDefault();
		setIsLoadingButtonUpdateGuideCategory(true);

		const data = {
			guide_category_title: e.target.title_category.value,
			guide_category_slug: e.target.title_slug_category.value,
			guide_category_description: e.target.description_category.value
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.patch(`${baseURL}/public/guides/categories/${e.target.id.value}`, data, options)
			.then(response => {
				// Update the selected guide in the data state
				/*const updatedGuideCategory = response.data;
				setSelectedGuideCateory(updatedRow);
				// Update the quidedata state with the updated row
				const updatedData = quidedata.data.map((row) => (row.id === updatedGuideCategory.result.id ? 
					{
						id: updatedGuideCategory.result.id,
						guide_category_title: updatedGuideCategory.result.guide_category_title,
						guide_category_slug: updatedGuideCategory.result.guide_category_slug,
						guide_category_description: updatedGuideCategory.result.guide_category_description
					}
				: row));
				quidedata.data = updatedData;*/
				// Update the quidedata state with the updated data
				// Close the offcanvas
				setShowUpdateGuideCategoryOffcanvas(false);
				setIsLoadingButtonUpdateGuideCategory(false);
				// Reset the form fields
				e.target.reset();
				// Show success notification
				handleShowNotification(setNotification, 'success', 'Erfolgreich', 'Deine Daten wurden erfolgreich gespeichert');
			})
			.catch((error) => {
				setShowUpdateGuideCategoryOffcanvas(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};

	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<Row>
				{/* Create Guide */}
				<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
					<div className="border-bottom pb-4 mb-4 d-flex align-items-center justify-content-end">
						<Button variant="secondary" className="me-2" onClick={handleShowCreateGuide}>
							{t('Create new Guide')}
						</Button>
					</div>
				</Col>
			</Row>
			<Row>
				<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
					<Tab.Container defaultActiveKey="all">
						<Card>
							<Card.Header className="border-bottom-0 p-0 bg-white">
								<Nav className="nav-lb-tab">
									<Nav.Item>
										<Nav.Link eventKey="all" className="mb-sm-3 mb-md-0">
											{t('All Guides & Resources')}
										</Nav.Link>
									</Nav.Item>
									{isLoadingGuideCategoryData ? (
										<Nav.Item>
											<Nav.Link eventKey="loading" className="mb-sm-3 mb-md-0">
												{t('Loading Categories...')}
											</Nav.Link>
										</Nav.Item>
									) : (
										GuideCategoryData?.length > 0 &&
										GuideCategoryData.map((category, index) => (
											<Nav.Item key={index}>
												<Nav.Link eventKey={category.guide_category_slug} className="mb-sm-3 mb-md-0">
													{category.guide_category_title}
												</Nav.Link>
											</Nav.Item>
										))
									)}
								</Nav>
							</Card.Header>
							<Card.Body className="p-0">
								<Tab.Content>
									<Tab.Pane eventKey="all" className="pb-4">
										<GuideSettingsTable activeKey={activeKey} guideData={GuideData} isLoadingTable={isLoadingTable} filterBy="" />
									</Tab.Pane>
									{isLoadingGuideCategoryData ? (
										<></>
									) : (
										GuideCategoryData?.length > 0 &&
										GuideCategoryData.map((category, index) => (
											<Tab.Pane key={index} eventKey={category.guide_category_slug} className="pb-4">
												<GuideSettingsTable activeKey={activeKey} guideData={GuideData} isLoadingTable={isLoadingTable} filterBy={category.guide_category_slug} />
											</Tab.Pane>
										))
									)}
								</Tab.Content>
							</Card.Body>
						</Card>
					</Tab.Container>
				</Col>
			</Row>
			{/* Create Guide Offcanvas */}
			<OffcanvasCreateGuide
				show={showCreateGuideOffcanvas}
				showCreateGuideCategoryOffcanvas={handleShowCreateGuideCategory}
				onHide={handleCloseCreateGuide}
				onSubmit={handleCreateGuide}
				isLoadingButton={isLoadingButtonCreateGuide}
			/>
			{/* Create Guide Category Offcanvas */}
			<OffcanvasCreateGuideCategory
				show={showCreateGuideCategoryOffcanvas}
				onHide={handleCloseCreateGuideCategory}
				onSubmit={handleCreateGuideCategory}
				isLoadingButton={isLoadingButtonCreateGuideCategory}
			/>
			{/* Update Guide Category Offcanvas */}
			<OffcanvasUpdateGuideCategory
				show={showUpdateGuideCategoryOffcanvas}
				onHide={handleCloseUpdateGuideCategory}
				selectedRow={selectedGuideCategory}
				onSubmit={handleUpdateGuideCategory}
				isLoadingButton={isLoadingButtonUpdateGuideCategory}
			/>
		</Fragment>
	);
};

export default AllGuideSettings;
